import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Shield, LogOut, User as UserIcon, LayoutDashboard, Receipt, Settings } from 'lucide-react';

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const dashboardPath = user?.role === 'admin' ? '/admin' : user?.role === 'seller' ? '/seller' : '/buyer';

  return (
    <header className="glass-header sticky top-0 z-50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
            <img
              src="/brand/icon.webp"
              alt="LeadXchange"
              className="w-8 h-8 rounded-sm object-contain"
            />
            <span className="font-heading text-xl font-semibold tracking-tight text-gray-900">
              LeadXchange
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <Link
              to="/marketplace"
              className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
              data-testid="nav-marketplace"
            >
              Marketplace
            </Link>
            <Link
              to="/pricing"
              className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
              data-testid="nav-pricing"
            >
              Pricing
            </Link>
            {user && (
              <Link
                to={dashboardPath}
                className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                data-testid="nav-dashboard"
              >
                Dashboard
              </Link>
            )}
            {user && user.role !== 'admin' && (
              <Link
                to="/payouts"
                className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                data-testid="nav-payouts"
              >
                {user.role === 'seller' ? 'Payouts' : 'Credits'}
              </Link>
            )}
          </nav>

          <div className="flex items-center gap-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-7 h-7 bg-[#0055FF] text-white rounded-full flex items-center justify-center text-xs font-medium">
                      {user.name?.[0]?.toUpperCase() || 'U'}
                    </div>
                    <span className="font-mono text-xs text-gray-700" data-testid="navbar-alias">{user.alias}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{user.name}</span>
                      <span className="font-mono text-xs text-gray-500 mt-0.5">{user.alias}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(dashboardPath)} data-testid="menu-dashboard">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                    <UserIcon className="w-4 h-4 mr-2" /> Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/verification')} data-testid="menu-verification">
                    <Shield className="w-4 h-4 mr-2" /> Trust Centre
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/transactions')} data-testid="menu-transactions">
                    <Receipt className="w-4 h-4 mr-2" /> Transactions
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                    <Settings className="w-4 h-4 mr-2" /> Settings
                  </DropdownMenuItem>
                  {user.role === 'buyer' && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/outcomes')} data-testid="menu-outcomes">
                        <Receipt className="w-4 h-4 mr-2" /> Outcomes
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/demand/new')} data-testid="menu-demand-new">
                        <Receipt className="w-4 h-4 mr-2" /> Post Demand
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/membership')} data-testid="menu-membership">
                        <Shield className="w-4 h-4 mr-2" /> Membership
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  data-testid="navbar-login-btn"
                  className="text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                >
                  Sign In
                </Button>
                <Button
                  onClick={() => navigate('/register')}
                  data-testid="navbar-register-btn"
                  className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
                >
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
